//region :: Imports ::
import React, {ReactElement, ReactNode} from 'react';
import MComponent from '../../Toolkit/Components/Component';
import {MCancelIconName, MIcon} from '../../Toolkit/Icons';
import {DialogPositionType} from 'primereact/dialog';
import Dialog from '../../Toolkit/Components/Controls/Dialog';
import MButton from '../../Toolkit/Components/Controls/Button';
import {FileTypeNames, DownloadIconName, FileIconName, ArchiveIconName} from '../SafeDigConstants';
import EnumContext from '../../Context/enum-context';

//endregion

/** */
type Props = {
    onDialogClose: (fileType: number, result: FileStatusResult) => void;
    jobComplete: boolean;
}

/** */
type State = {
    position?: DialogPositionType;
    visible: boolean;
    data: FileData;
    uploadPermission: boolean;
    appArea: number;
}

export type FileData = {
    fileType: number | undefined;
    estimate: string | undefined;
    fileInfo: string | undefined;
    expiryDate: Date | undefined;
}

export enum FileStatusResult {
    Archive,
    Download,
    Cancel
}

/** */
class FileStatusDialog extends MComponent<Props, State> {
    static contextType = EnumContext;
    context!: React.ContextType<typeof EnumContext>

    private FileIcon: MIcon = {name: FileIconName, color: 'mcolor-info'};

    /** */
    public constructor(props: Props) {
        super(props);

        this.bindMethods();
        this.initializeState();
    }

    /** */
    private bindMethods(): void {
        this.dialog_onHide = this.dialog_onHide.bind(this);

        this.getDialogTemplate = this.getDialogTemplate.bind(this);
        this.getFooterTemplate = this.getFooterTemplate.bind(this);
        this.archive_Click = this.archive_Click.bind(this);
        this.download_Click = this.download_Click.bind(this);
        this.close_Click = this.close_Click.bind(this);
        this.isArchiveDisabled = this.isArchiveDisabled.bind(this);

        this.show = this.show.bind(this);
        this.hide = this.hide.bind(this);
    }

    /** */
    private initializeState(): void {
        this.state = {
            visible: false,
            data: {
                fileType: undefined,
                estimate: undefined,
                fileInfo: undefined,
                expiryDate: undefined
            },
            uploadPermission: false,
            appArea: 0
        };
    }

    /** */
    private dialog_onHide(): void {
        this.safeSetState((draft: State) => draft.visible = false);
    }

    /** */
    public show(fileData: FileData, uploadPermission: boolean, appArea: number = 0, position?: DialogPositionType) {

        this.safeSetState((draft: State) => {
            draft.position = position;
            draft.visible = true;
            draft.data = fileData;
            draft.uploadPermission = uploadPermission;
            draft.appArea = appArea;
        });

    }

    private archive_Click() {
        this.props.onDialogClose(this.state.data.fileType!, FileStatusResult.Archive);
        this.hide();
    }

    private download_Click() {
        this.props.onDialogClose(this.state.data.fileType!, FileStatusResult.Download);
        this.hide();
    }

    private close_Click() {
        this.props.onDialogClose(this.state.data.fileType!, FileStatusResult.Cancel);
        this.hide();
    }

    private hide() {
        this.safeSetState((draft: State) => {
            draft.visible = false;
        });
    }

    private getDialogTemplate(): ReactNode {
        let fileType = 'Unknown';
        let title = '';

        if(this.state.appArea === 0) {
            fileType = FileTypeNames[this.state.data.fileType!];
            title = 'Line Search file for estimate';
        } else if(this.state.appArea === 1) {
            const index = this.context?.enums?.noticeFileTypes.findIndex(n => n.id === this.state.data.fileType)
            fileType = index === undefined ? '' : this.context?.enums?.noticeFileTypes[index]?.name ?? '';
            title = 'Notice file for id';
        } else {
            fileType = '';
        }

        return (
            <div>
                <p>{`${fileType} ${title} ${this.state.data.estimate}`}</p>
                <p>{`File Info: ${this.state.data.fileInfo}`}</p>
                <p>{`Expiry Date: ${this.state.data.expiryDate?.toLocaleDateString()}`}</p>
            </div>
        );
    }

    private getFooterTemplate(): ReactNode {
        return (
            <React.Fragment>
                <MButton label="Archive"
                         icon={ArchiveIconName}
                         disabled={this.isArchiveDisabled}
                         onClick={this.archive_Click}/>
                <MButton label="Download"
                         icon={DownloadIconName}
                         disabled={false}
                         onClick={this.download_Click}/>
                <MButton label="Close"
                         icon={MCancelIconName}
                         disabled={false}
                         onClick={this.close_Click}/>
            </React.Fragment>
        );
    }

    private isArchiveDisabled() {
        return !this.state.uploadPermission || this.props.jobComplete;
    }

    /** */
    public render(): ReactElement {

        const title = this.state.appArea === 0 ?
                                           'Line Search File Status':
                                           'Noticing File Status';

        return (
            <Dialog title={title}
                    content={this.getDialogTemplate()}
                    icon={this.FileIcon}
                    footer={this.getFooterTemplate()}
                    position={this.state.position}
                    canClose={false}
                    closeOnEscape={false}
                    visible={this.state.visible}
                    onHide={this.dialog_onHide}/>
        );

    }

}

export default FileStatusDialog;
